import * as CryptoJS from 'crypto-js';
class StorageService {
  constructor(settings) {
    /**
     * @description Sets a callback when session expires.
     */
    this.sessionExpiryCallback = callback => {
      this.sessionExpiryCallbackFn = callback;
    };
    /**
     * @description
     * @param [minutes] Default extension is sessionTimeToLive
     */
    this.extendSession = minutes => {
      if (!minutes) {
        minutes = this.settings.sessionTimeToLive;
      }
      this.setExpiry(minutes);
    };
    /**
     * @description Destroy the session. SessionCallback will not be executed
     */
    this.killSession = () => {
      clearTimeout(this.timeout);
      localStorage.removeItem('session');
      this.expiry = new Date();
      this.setCookie('session_expiry', '');
      this.setCookie('session_code', '');
      this.initializeSession();
    };
    this.set = (key, value) => {
      const data = {};
      data[key] = value;
      this.setMany(data);
    };
    this.setMany = items => {
      if (this.settings.sessionAutoExtend) {
        this.extendSession();
      }
      const data = this.getData();
      Object.keys(items).forEach(i => {
        data[i] = JSON.stringify(items[i]);
      });
      let encodedData = JSON.stringify(data);
      const code = this.getCookie('session_code');
      if (code) {
        encodedData = CryptoJS.AES.encrypt(encodedData, code);
      }
      localStorage.setItem('session', encodedData);
    };
    this.get = key => {
      if (this.settings.sessionAutoExtend) {
        this.extendSession();
      }
      const data = this.getData();
      if (!data || data[key] === undefined) {
        return undefined;
      }
      return JSON.parse(data[key]);
    };
    this.setCookie = (key, value) => {
      document.cookie = `${key}=${window.escape(JSON.stringify(value))};expires='${this.expiry.toUTCString()}'; secure; samesite=strict; path=/`;
    };
    this.getCookie = key => {
      const decodedCookie = decodeURIComponent(document.cookie);
      const pairs = decodedCookie.split(/;\s*/);
      const prefix = `${key}=`;
      for (const pair of pairs) {
        if (pair.indexOf(prefix) === 0) {
          return JSON.parse(pair.substring(prefix.length));
        }
      }
    };
    this.setExpiry = minutes => {
      const expiryDate = new Date();
      expiryDate.setMinutes(expiryDate.getMinutes() + minutes);
      this.expiry = expiryDate;
      this.setCookie('session_expiry', this.expiry);
    };
    this.getData = () => {
      const dataRaw = localStorage.getItem('session');
      if (dataRaw == null) {
        return {};
      }
      let dataString;
      const code = this.getCookie('session_code');
      if (code) {
        dataString = CryptoJS.AES.decrypt(dataRaw, code).toString(CryptoJS.enc.Utf8);
      } else {
        dataString = dataRaw;
      }
      try {
        return JSON.parse(dataString);
      } catch {
        return {};
      }
    };
    settings != null ? this.settings = settings : this.settings = {};
    // Setting defaults
    if (this.settings.useEncryption == null) {
      this.settings.useEncryption = true;
    }
    if (this.settings.sessionTimeToLive == null) {
      this.settings.sessionTimeToLive = 30;
    }
    if (this.settings.sessionAutoExtend == null) {
      this.settings.sessionAutoExtend = false;
    }
    // Initializing timeout & cookies
    this.initializeSession();
  }
  initializeSession() {
    const expireDate = this.getCookie('session_expiry');
    if (expireDate) {
      // existing session
      const diff = new Date(expireDate).getTime() - new Date().getTime();
      if (diff < 0) {
        // already expired
        this.setExpired();
      } else {
        this.expiry = new Date(expireDate);
        const diffInMinutes = Math.ceil(diff / (60 * 1000));
        this.createTimeout(diffInMinutes);
      }
    } else {
      // new session
      localStorage.removeItem('session');
      this.setExpiry(this.settings.sessionTimeToLive);
      this.createTimeout(this.settings.sessionTimeToLive);
      let code = '';
      if (this.settings.useEncryption === true) {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        for (let i = 0; i < 32; i++) {
          code += characters.charAt(Math.floor(Math.random() * characters.length));
        }
      }
      this.setCookie('session_code', code);
    }
  }
  setExpired() {
    this.killSession();
    if (this.sessionExpiryCallbackFn) {
      this.sessionExpiryCallbackFn();
    }
  }
  createTimeout(minutes) {
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      this.setExpired();
    }, minutes * 60 * 1000);
  }
}

/*
 * Public API Surface of storage-service
 */

/**
 * Generated bundle index. Do not edit.
 */

export { StorageService };
